import type { MappedMarketsUpdates, MarketId } from '@ps/isp-web-services-types';
import React, { useMemo, useRef } from 'react';
import { useStore } from 'react-redux';
import { BehaviorSubject, EMPTY, Observable, from, interval } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  scan,
  shareReplay,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';

import { request } from '@swf/core/api/request';
import useUnmount from '@swf/core/hooks/useUnmount';
import type { RootState } from '@swf/core/store';

import buildSportsRequest from '../utils/buildSportsRequest';

import type { MarketPollingData } from './../types/market';

export interface MarketPollingContextValue {
  add: (marketIds: MarketId[]) => void;
  remove: (marketIds: MarketId[]) => void;
  marketsObservable: Observable<MarketPollingData>;
}

interface ConsumerPayload {
  key: 'add' | 'remove';
  value: MarketId[];
}

export const findClosedMarkets = (
  requestIds: MarketId[],
  receivedData: MappedMarketsUpdates
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
): MarketId[] => requestIds.filter((id) => !receivedData[id]);

export const consumerReducer = (
  previous: MarketId[],
  payload: ConsumerPayload | null
): MarketId[] => {
  if (!payload) return previous;

  if (payload.key === 'add') {
    return previous.concat(payload.value);
  }

  const marketIds = [...previous];
  payload.value.forEach((a) => {
    const index = marketIds.findIndex((b) => a === b);

    if (index >= 0) {
      marketIds.splice(index, 1);
    }
  });

  return marketIds;
};

const fetchMarkets = (
  marketIds: MarketId[],
  unmount: ReturnType<typeof useUnmount>,
  getState: () => RootState
): Observable<MarketPollingData> => {
  const rootState = getState();
  const sportsRequest = buildSportsRequest(request, rootState);

  return from(
    sportsRequest<MappedMarketsUpdates>({
      path: '/markets-updates?priceHistory=1' as const,
      method: 'POST' as const,
      body: [...new Set(marketIds)] // Remove duplicates
    })
  ).pipe(
    takeUntil(unmount),
    map(
      (markets): MarketPollingData => ({
        data: markets.body,
        closed: findClosedMarkets(marketIds, markets.body)
      })
    ),
    catchError(() => EMPTY)
  );
};

export const MarketPollingContext = React.createContext<MarketPollingContextValue>({
  add: () => undefined,
  remove: () => undefined,
  marketsObservable: new Observable<MarketPollingData>()
});

const POLLING_INTERVAL = 5000;

const MarketPollingProvider: React.FC<
  {
    value?: MarketPollingContextValue;
  } & React.PropsWithChildren
> = ({ children, ...props }) => {
  const unmount = useUnmount();

  const { getState } = useStore<RootState>();

  const consumers = useRef(new BehaviorSubject<ConsumerPayload | null>(null));

  const marketsObservable = useRef(
    interval(POLLING_INTERVAL).pipe(
      // Stop polling if this component unmounts
      takeUntil(unmount),
      // Reduce consumers to a unique array of market IDs
      withLatestFrom(consumers.current.pipe(scan(consumerReducer, []))),
      // Only run if we have marketIds to fetch data for
      filter(([, marketIds]) => marketIds.length > 0),
      // Call fetchMarkets once
      mergeMap(([, marketIds]) => fetchMarkets(marketIds, unmount, getState)),
      // Make the observer a singleton to avoid making duplicate requests on every new subscriber
      shareReplay()
    )
  );

  const value = useMemo(
    () => ({
      add: (marketIds: MarketId[]) => {
        consumers.current.next({ key: 'add', value: marketIds });
      },
      remove: (marketIds: MarketId[]) => {
        consumers.current.next({ key: 'remove', value: marketIds });
      },
      marketsObservable: marketsObservable.current
    }),
    []
  );

  return (
    <MarketPollingContext.Provider value={props.value || value}>
      {children}
    </MarketPollingContext.Provider>
  );
};

export default MarketPollingProvider;
